import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { SmeWebTelemetry } from '../diagnostics/sme-web-telemetry';
import { Strings } from '../generated/strings';
import { EnvironmentModule } from '../manifest/environment-modules';
import { AuthorizationManager, AuthorizationToken } from '../security/authorization-manager';
import { GatewayConnection, GatewayRequest } from './gateway-connection';
import { GatewayUrlTranslator } from './gateway-url-translator';
import { headerConstants } from './http-constants';
import { Net } from './net';
import { PowerShellCommandWithoutState } from './powershell';

/**
 * Node Cim output type.
 */
export enum NodeCimOutput {
    /**
     * Single instance.
     */
    Single,

    /**
     * Multiple instances.
     */
    Multiple,

    /**
     * Invoke result.
     */
    Result,

    /**
     * WQL query result.
     */
    Query
}

/**
 * Node Request options that we use to extend the GatewayRequest interface
 */
export interface NodeRequestOptions {
    /**
     * Indicates that no auth failure handling should take place, however, any auth headers for this node will still be added.
     */
    noAuth?: boolean;

    /**
     * Provides an alternative token to use for just this request
     */
    authToken?: AuthorizationToken;

    /**
     * Authentication mechanism.
     */
    authenticationMechanism?: 'Credssp' | 'NotSpecified';

    /**
     * Indicates if command should run in local runspace instead of remote runspace.
     */
    useInProcRunspace?: boolean;

    /**
     * Indicates that audit logging for this request should be made. Default is false.
     */
    logAudit?: boolean;

    /**
     * Indicates that telemetry logging for this request should be made. Default is false.
     * SmeWebTelemetry will not be sent for polling PS events.
     */
    logTelemetry?: boolean;

    /**
     * Indicates targeting to specific PowerShell endpoint.
     * The endpoint data is inserted to the endpoint header.
     */
    powerShellEndpoint?: string;

    /**
     * The PowerShell command resource on JEA PowerShell endpoint.
     * The command object is used for auto fallback time on a CIM query.
     * (Only CIM query uses this property)
     */
    powerShell?: PowerShellCommandWithoutState;

    /**
     * The parameters for instance query powershell alternative.
     * (Only CIM query uses this property)
     */
    powerShellParameters?: any;

    /**
     * The PowerShell context to replace CIM call.
     * (Only CIM query uses this property)
     */
    powerShellContext?: {
        /**
         * CIM output data type.
         */
        cimOutput: NodeCimOutput;

        /**
         * CIM input parameters.
         */
        parameters?: any;
    };
}

/**
 * Extension of GatewayRequest interface for calling the Gateway Node API
 */
export interface NodeRequest extends GatewayRequest, NodeRequestOptions {
}

/**
 * Service request interface for gateway version 2.
 */
export interface ServiceRequest {
    /**
     * The service name such as "WinRest", "WinStream", "LinuxBase" and so on.
     */
    serviceName: string;

    /**
     * The controller name such as "PowerShell", "Cim", "Ssh" and so on.
     */
    controllerName: string;

    /**
     * The node name.
     */
    nodeName: string;
}

/**
 * The Node Connection class for creating requests and calling the Gateway's Node API
 */
export class NodeConnection {
    /**
     * Initializes a new instance of the GatewayService class.
     *
     * @param gateway the gateway Connection
     * @param authorizationManager the authorization manager.
     */
    constructor(private gateway: GatewayConnection, private authorizationManager: AuthorizationManager) { }

    /**
     * Makes a POST call to the gateway node api
     *
     * @param serviceRequest the object of service request.
     * @param remained the remained Url after 'nodes/<nodeName>'.
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public post(serviceRequest: ServiceRequest, remained: string, body?: any, request?: NodeRequest): Observable<any>;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a POST call to the gateway node api
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     */
    public post(nodeName: string, relativeUrl: string, body?: any, request?: NodeRequest): Observable<any>;
    public post(nodeOrService: string | ServiceRequest, remained: string, body?: any, request?: NodeRequest): Observable<any> {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request = this.createNodeRequest(request || <NodeRequest>{}, nodeName);
        return this.gateway.post(relativeUrl, body, request);
    }

    /**
     * Makes a GET call to the gateway node api
     *
     * @param serviceRequest the object of service request.
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public get(serviceRequest: ServiceRequest, relativeUrl: string, request?: NodeRequest): Observable<any>;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a GET call to the gateway node api
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param request the node request object.
     */
    public get(nodeName: string, relativeUrl: string, request?: NodeRequest): Observable<any>;
    public get(nodeOrService: string | ServiceRequest, remained: string, request?: NodeRequest): Observable<any> {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request = this.createNodeRequest(request || <NodeRequest>{}, nodeName);
        return this.gateway.get(relativeUrl, request);
    }

    /**
     * Makes a PUT call to the gateway node api
     *
     * @param serviceRequest the object of service request.
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public put(serviceRequest: ServiceRequest, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a PUT call to the gateway node api
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     */
    public put(nodeName: string, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    public put(nodeOrService: string | ServiceRequest, remained: string, body?: string, request?: NodeRequest): Observable<any> {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request = this.createNodeRequest(request || <NodeRequest>{}, nodeName);
        return this.gateway.put(relativeUrl, body, request);
    }

    /**
     * Makes a PATCH call to the gateway node api
     *
     * @param serviceRequest the object of service request.
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public patch(serviceRequest: ServiceRequest, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a PATCH call to the gateway node api
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     */
    public patch(nodeName: string, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    public patch(nodeOrService: string | ServiceRequest, remained: string, body?: string, request?: NodeRequest): Observable<any> {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request = this.createNodeRequest(request || <NodeRequest>{}, nodeName);
        return this.gateway.patch(relativeUrl, body, request);
    }

    /**
     * Makes a DELETE call to the gateway node api
     *
     * @param serviceRequest the object of service request.
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public delete(serviceRequest: ServiceRequest, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a DELETE call to the gateway node api
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object.
     */
    public delete(nodeName: string, relativeUrl: string, body?: string, request?: NodeRequest): Observable<any>;
    public delete(nodeOrService: string | ServiceRequest, remained: string, body?: string, request?: NodeRequest): Observable<any> {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request = this.createNodeRequest(request || <NodeRequest>{}, nodeName);
        return this.gateway.delete(relativeUrl, body, request);
    }

    /**
     * Makes a DELETE call to the gateway node api without waiting for the response.
     *
     * @param serviceRequest the object of service request.
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param request the node request object.
     * @remarks this API is for gateway version 2. It doesn't work on gateway version 1.
     */
    public deleteQuick(serviceRequest: ServiceRequest, relativeUrl: string, request?: NodeRequest): void;
    /**
     * @deprecated This API is for version 1 gateway server, use new API for version 2 gateway.
     * Makes a DELETE call to the gateway node api without waiting for the response.
     *
     * @param nodeName the name of the node to call the API for
     * @param relativeUrl the relative Url after "/api/nodes/<nodeName>"
     * @param request the node request object.
     */
    public deleteQuick(nodeName: string, relativeUrl: string, request?: NodeRequest): void;
    public deleteQuick(nodeOrService: string | ServiceRequest, remained: string, request?: NodeRequest): void {
        const { nodeName, relativeUrl } = this.getNodeUrl(nodeOrService, remained);
        request.headers = request.headers || {};
        if (!request.authToken) {
            // if we don't have an authToken, then make sure we are not waiting on one.
            this.authorizationManager.addAuthorizationRequestHeader(request, nodeName);
        } else {
            this.authorizationManager.addAuthorizationRequestHeader(request, nodeName, request.authToken);
        }

        const endpoint = this.authorizationManager.getJeaEndpoint(nodeName);
        if (request.powerShellEndpoint) {
            // Override JEA fallback if endpoint is explicitly set for debugging purposes
            (<any>request).headers[headerConstants.POWERSHELL_ENDPOINT] = request.powerShellEndpoint;
        } else if (endpoint) {
            (<any>request).headers[headerConstants.POWERSHELL_ENDPOINT] = endpoint;
        }

        if (request.authenticationMechanism === 'Credssp') {
            (<any>request).headers[headerConstants.AUTHENTICATION_MECHANISM] = request.authenticationMechanism;
        }

        return this.gateway.deleteQuick(relativeUrl, request.headers);
    }

    /**
     * Persists the JEA powershell endpoint context
     * @param nodeName The node name
     * @param endpoint The powershell endpoint
     */
    public saveJeaContext(nodeName: string, endpoint: string) {
        this.authorizationManager.saveJeaContext(nodeName, endpoint);
    }

    /**
     * Gets the JEA powershell endpoint, if it exists
     * @param nodeName The node name
     */
    public getJeaEndpoint(nodeName: string) {
        return this.authorizationManager.getJeaEndpoint(nodeName);
    }

    /**
     * Creates service node API for Gateway V2.
     *
     * @param serviceName the name of service.
     * @param apiName the name of api.
     * @param nodeName the name of node.
     * @param remained the remained URL after it including option parameters.
     * @returns URL string.
     */
    public getServiceUrl(serviceName: string, apiName: string, nodeName: string, remained: string): string {
        // 1) if null or empty, it's empty.
        // 2) if start with "?", remains as is.
        // 3) if not started with "/", add "/".
        remained = remained ?? '';
        if (remained.length > 0 && !remained.startsWith('/')) {
            if (!remained.startsWith('?')) {
                remained = `/${remained}`;
            }
        }

        // for example, "/api/services/WinREST/PowerShell/nodes/computer-abc/invoke"
        return `/Services/${serviceName}/${apiName}/nodes/${nodeName}${remained}`;
    }

    /**
     * Adds default parameters to a NodeRequest
     *
     * @param method the http method to use
     * @param relativeUrl the relative Url after "/api/"
     * @param body the body string JSON.stringfy'ed
     * @param request the node request object to extend.
     */
    private createNodeRequest(request: NodeRequest, nodeName: string): GatewayRequest {
        // if we did not specify no auth, and we are not using a custom token...
        request = MsftSme.deepAssign({}, request);
        if (!request.noAuth && !request.authToken) {
            // ...then add node specific authorization handlers
            request.retryHandlers = (request.retryHandlers || []).concat([{
                canHandle: (code, error) => this.authorizationManager.canHandleAjaxFailure(code, error),
                handle: (code, originalRequest, error) =>
                    this.authorizationManager.handleAjaxFailure(code, originalRequest, error, nodeName)
            }]);
        }

        const oldBeforeCall = request.beforeCall;

        // Add before call handler to wait for any pending node authorization
        request.beforeCall = pendingRequest => {
            let observable: Observable<void> = null;
            if (!request.authToken) {
                // if we dont have an authToken, then make sure we are not waiting on one.
                observable = this.authorizationManager.authAwaiter
                    .pipe(map(() =>
                        this.authorizationManager.addAuthorizationRequestHeader(pendingRequest, nodeName)));
            } else {
                observable = of(
                    this.authorizationManager.addAuthorizationRequestHeader(pendingRequest, nodeName, request.authToken)
                );
            }

            if (oldBeforeCall) {
                return oldBeforeCall(request).pipe(mergeMap(() => observable));
            }

            return observable;
        };

        request.headers = request.headers || {};
        if (request.logAudit === true || request.logAudit === false) {
            (<any>request).headers[headerConstants.LOG_AUDIT] = request.logAudit ? 'true' : 'false';
        }

        if (request.logTelemetry === true || request.logTelemetry === false) {
            (<any>request).headers[headerConstants.LOG_TELEMETRY] = request.logTelemetry ? 'true' : 'false';
        }

        const endpoint = this.authorizationManager.getJeaEndpoint(nodeName);
        if (request.powerShellEndpoint) {
            // Override JEA fallback if endpoint is explicitly set for debugging purposes
            (<any>request).headers[headerConstants.POWERSHELL_ENDPOINT] = request.powerShellEndpoint;
        } else if (endpoint) {
            (<any>request).headers[headerConstants.POWERSHELL_ENDPOINT] = endpoint;
        }

        if (request.authenticationMechanism === 'Credssp') {
            (<any>request).headers[headerConstants.AUTHENTICATION_MECHANISM] = request.authenticationMechanism;
        }

        return request;
    }

    /**
     * Get node name and node url.
     * @param nodeOrService The node name or service request object.
     * @param remained The remained url.
     * @returns node name and node url.
     */
    private getNodeUrl(nodeOrService: string | ServiceRequest, remained: string): { nodeName: string, relativeUrl: string } {
        if (typeof nodeOrService === 'string') {
            const nodeName = <string>nodeOrService;
            return { nodeName, relativeUrl: this.getNodeMappedUrl(nodeName, remained) };
        }

        const service = <ServiceRequest>nodeOrService;
        return {
            nodeName: service.nodeName,
            relativeUrl: this.getServiceUrl(service.serviceName, service.controllerName, service.nodeName, remained)
        };
    }

    /**
     * Creates a Node url
     *
     * @param nodeName the name of the node to make a call against
     * @param relativeUrl the relative Url after "/nodes/<nodeName>/"
     */
    private getNodeMappedUrl(nodeName: string, relativeUrl: string): string {
        if (EnvironmentModule.isGatewayV200) {
            const translation = GatewayUrlTranslator.translate(relativeUrl);
            if (translation) {
                return `${translation.startUrl}/nodes/${nodeName}/${translation.newUrl}`;
            }

            SmeWebTelemetry.traceAction(null, {
                content: {
                    message: 'Could not get translation for URL: {0}'.format(relativeUrl),
                    action: 'TranslateUrlToGateWayV2'
                }
            },
                { type: 'error' });

            throw new Error(MsftSme.getStrings<Strings>().MsftSmeShell.Core.TranslateGatewayUrls.Url.Not.expected.ErrorMessage
                .format(relativeUrl)
            );
        }

        // create node url from current url
        if (!relativeUrl.startsWith('/')) {
            relativeUrl = `/${relativeUrl}`;
        }

        relativeUrl = Net.updateApiVersion20190201(relativeUrl);
        return `/nodes/${nodeName}${relativeUrl}`;
    }
}
